import { useEffect, useMemo, useState } from "react";
import {
  buildSidebarPages,
  notifySidebarSessionUpdated,
  readStorageJson,
  readStorageNumber,
  readStorageValue,
  SIDEBAR_SESSION_EVENT,
  uniqueBy,
} from "../utils/sidebarStorage";

function readSidebarSession() {
  const legalEntities = uniqueBy(
    readStorageJson("Entities", []),
    (entity) => `${entity?.LegalEntityID}|${entity?.LegalEntityName}`,
  );
  const merchants = uniqueBy(
    readStorageJson("Merchants", []),
    (merchant) => `${merchant?.MerchantID}|${merchant?.MerchantName}`,
  );
  const features = readStorageJson("Features", []);
  const tabs = readStorageJson("Tabs", []);
  const sidebarPages = readStorageJson(
    "SidebarPages",
    buildSidebarPages(features, tabs),
  );
  const entityId = readStorageNumber("EntityID");
  const legalEntityId = readStorageNumber("LegalEntityID");
  const entityType = readStorageValue("EntityType");
  const matchedLegalEntity = legalEntities.find(
    (entity) =>
      entity?.LegalEntityID === legalEntityId || entity?.LegalEntityID === entityId,
  );
  const matchedMerchant = merchants.find(
    (merchant) => merchant?.MerchantID === entityId,
  );
  const resolvedUserImageUrl =
    readStorageValue("UserIconUrl") ||
    matchedMerchant?.UserIconUrl ||
    matchedLegalEntity?.UserIconUrl ||
    "";

  return {
    sessionVersion: Date.now(),
    entityType,
    entityRole: readStorageValue("EntityRole"),
    entityId,
    legalEntityId,
    entityName: readStorageValue("EntityName", "Account"),
    entityImageUrl:
      readStorageValue("EntityURL") ||
      readStorageValue("MerchantIconURL") ||
      readStorageValue("LegalEntityIconUrl"),
    userName: readStorageValue("UserName", "User"),
    userImageUrl: resolvedUserImageUrl,
    bslStatus: readStorageNumber("BslStatus"),
    features,
    tabs,
    sidebarPages,
    legalEntities,
    merchants,
  };
}

export default function useSidebarSession() {
  const [session, setSession] = useState(() => readSidebarSession());

  useEffect(() => {
    const updateSession = () => setSession(readSidebarSession());

    window.addEventListener("storage", updateSession);
    window.addEventListener(SIDEBAR_SESSION_EVENT, updateSession);

    return () => {
      window.removeEventListener("storage", updateSession);
      window.removeEventListener(SIDEBAR_SESSION_EVENT, updateSession);
    };
  }, []);

  return useMemo(() => session, [session]);
}

export { notifySidebarSessionUpdated };
